import { useQuery } from '@apollo/client';
import { transformError } from '@core/adapters/errors/transformError';
import { useMemo } from 'react';

import { GET_AUDIT_DECISIONS } from '../graphql/queries/getAuditDecisions';

interface GetAuditDecisionsResponse {
  auditDecisions: {
    decisions: Array<{
      id: string;
      type: string;
      createdBy: string;
      createdAt: string;
      lastModifiedBy: string;
      lastModifiedAt: string;
      auditItemId: string;
      auditSessionId: string;
      auditorId: string;
      auditorName: string;
      timestamp: string;
      updatedAt?: string;
      isSelected: boolean;
      selectedBy?: string;
      selectedAt?: string;
      isCurrentUser: boolean;
      languageCorrect: boolean;
      transcriptionCorrect: boolean;
      correctedTranscription?: string;
      ocrCorrect: boolean;
      correctedOcr?: string;
      originalLabels: Array<{
        name: string;
        severity: number;
      }>;
      correctedLabels: Array<{
        name: string;
        severity: number;
      }>;
      comments?: string;
      labelsCorrect?: boolean;
      policiesCorrect?: boolean;
      triggeredPolicyAssessments?: Array<{
        policyId: string;
        policyName: string;
        isCorrect: boolean;
        comments?: string;
      }>;
      nonTriggeredPolicyAssessments?: Array<{
        policyId: string;
        policyName: string;
        isCorrect: boolean;
        comments?: string;
      }>;
    }>;
  };
}

/**
 * Hook for fetching all audit decisions for a session
 * Part of the 4-query architecture - fetches only decisions data
 */
export function useGetAuditDecisions(sessionId: string | undefined) {
  const { data, loading, error, refetch } = useQuery<GetAuditDecisionsResponse>(GET_AUDIT_DECISIONS, {
    variables: { auditSessionId: sessionId },
    skip: !sessionId,
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network', // Instant cache + fresh network data on first load
    nextFetchPolicy: 'cache-first', // Subsequent queries use cache-first
    notifyOnNetworkStatusChange: false, // Don't show loading during background refetches
  });

  // Extract decisions data
  const decisions = useMemo(() => data?.auditDecisions?.decisions || [], [data]);

  // Transform error using adapter pattern
  const domainError = useMemo(() => {
    if (!error) return undefined;

    const mappedError = transformError(error);

    return {
      ...mappedError,
      onRetry: () => refetch(),
    };
  }, [error, refetch]);

  return {
    decisions,
    loading,
    error: domainError,
    refetch,
  };
}
